import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, message, Empty } from "antd";
import { formatDate, generateRefNumber } from "../../Utils/formatters";
import { UserDataGetwt } from "../../../../Api/CoreApi";

import "./RecentMatches.css";

function RecentMatches({ data = null }) {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Helper - Get profile ID
  const getProfileId = (user) => {
    if (user.profile_id) return user.profile_id;
    if (user.user?.profile_id) return user.user.profile_id;
    return '⚠️ N/A';
  };

  // ✅ Helper - Get user image
  const getUserImage = (user) => {
    if (user.image && typeof user.image === 'string') {
      if (user.image.startsWith('http')) return user.image;
      return user.image;
    }
    if (user.pic && typeof user.pic === 'string') {
      if (user.pic.startsWith('http')) return user.pic;
      return user.pic;
    }
    const name = user.firstname || user.first_name || user.username || 'User';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=ff0055&color=fff&size=128`;
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setMatches(data.slice(0, 5));
    } else {
      fetchMatches();
    }
  }, [data]);

  // ⭐ MAIN LOGIC - BiodataMatches ki tarah pair banao
  const fetchMatches = async () => {
    setLoading(true);
    try {
      const response = await UserDataGetwt();
      
      console.log('📦 Raw Response:', response);
      
      // ⭐ FIX: Handle different response formats
      let allUsers = [];
      
      if (Array.isArray(response)) {
        allUsers = response;
      } else if (response && Array.isArray(response.data)) {
        allUsers = response.data;
      } else if (response && Array.isArray(response.results)) {
        allUsers = response.results;
      } else if (response && Array.isArray(response.users)) {
        allUsers = response.users;
      } else if (typeof response === 'object' && response !== null) {
        const arrayValue = Object.values(response).find(v => Array.isArray(v));
        if (arrayValue) {
          allUsers = arrayValue;
        }
      }
      
      console.log('📊 Total users:', allUsers.length);

      if (!allUsers || allUsers.length === 0) {
        console.warn('⚠️ No users found');
        setMatches([]);
        setLoading(false);
        return;
      }

      // Filter only regular users
      const regularUsers = allUsers.filter(u => {
        const role = (u.role || u.user?.role || 'user').toLowerCase();
        return role === 'user' || !role;
      });

      // Separate by gender
      const males = regularUsers.filter(u => 
        u.gender?.toLowerCase() === 'male'
      );
      const females = regularUsers.filter(u => 
        u.gender?.toLowerCase() === 'female'
      );

      console.log(`   Males: ${males.length} | Females: ${females.length}`);

      // Find matched pairs
      const matchedPairs = [];
      const usedFemales = new Set();
      let matchId = 1;

      for (const male of males) {
        if (matchedPairs.length >= 10) break;

        const maleReligion = (male.religion || '').toLowerCase().trim();
        const maleCaste = (male.caste || male.cast || '').toLowerCase().trim();

        if (!maleReligion || !maleCaste) continue;

        for (const female of females) {
          if (usedFemales.has(female.id)) continue;

          const femaleReligion = (female.religion || '').toLowerCase().trim();
          const femaleCaste = (female.caste || female.cast || '').toLowerCase().trim();

          if (maleReligion === femaleReligion && maleCaste === femaleCaste) {
            let matchPct = 40;
            
            if (male.state && female.state && 
                male.state.toLowerCase() === female.state.toLowerCase()) {
              matchPct += 20;
            }
            
            if (male.city && female.city && 
                male.city.toLowerCase() === female.city.toLowerCase()) {
              matchPct += 15;
            }
            
            if (male.district && female.district && 
                male.district.toLowerCase() === female.district.toLowerCase()) {
              matchPct += 10;
            }
            
            if (male.course && female.course && 
                male.course.toLowerCase() === female.course.toLowerCase()) {
              matchPct += 10;
            }
            
            if (male.age && female.age) {
              const ageDiff = Math.abs(male.age - female.age);
              if (ageDiff <= 5) matchPct += 5;
            }
            
            matchPct = Math.min(matchPct, 100);

            const statuses = ['accepted', 'pending', 'suggested'];
            const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];

            matchedPairs.push({
              id: matchId,
              match_id: `MATCH-${String(matchId).padStart(5, '0')}`,
              
              profile1_name: male.firstname || male.first_name || male.username || 'User',
              profile1_id: male.User_id || male.user_id || male.id,
              profile1_profile_id: getProfileId(male),
              profile1_age: male.age || 0,
              profile1_city: male.city || male.district || '',
              profile1_state: male.state || '',
              profile1_image: getUserImage(male),
              
              profile2_name: female.firstname || female.first_name || female.username || 'User',
              profile2_id: female.User_id || female.user_id || female.id,
              profile2_profile_id: getProfileId(female),
              profile2_age: female.age || 0,
              profile2_city: female.city || female.district || '',
              profile2_state: female.state || '',
              profile2_image: getUserImage(female),
              
              religion: male.religion,
              caste: male.caste || male.cast,
              match_percentage: matchPct,
              matchDate: male.create_date || male.created_at || new Date().toISOString(),
              status: randomStatus,
            });

            usedFemales.add(female.id);
            matchId++;
            break;
          }
        }
      }

      console.log(`   ✅ Found ${matchedPairs.length} matched pairs`);
      setMatches(matchedPairs);

    } catch (error) {
      console.error("❌ Error:", error);
      message.error("Failed to load matches");
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = () => navigate('/admin/users');

  const getStatusStyle = (status) => {
    const styles = {
      'accepted': { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
      'pending': { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
      'rejected': { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
      'suggested': { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
    };
    return styles[status] || styles.suggested;
  };

  return (
    <div className="recent-matches-card">
      <div className="table-header">
        <h3 className="table-title">Recent Matches</h3>
        {matches.length > 0 && (
          <span style={{
            fontSize: 11,
            padding: '3px 10px',
            background: '#fff0f3',
            color: '#ff0055',
            borderRadius: 999,
            border: '1px solid #ffd3df',
            fontWeight: 700,
            marginLeft: 'auto'
          }}>
            🎯 {matches.length} matched pairs
          </span>
        )}
      </div>

      <div className="table-wrapper">
        {loading ? (
          <div className="matches-loading">
            <Spin size="large" />
            <p>Finding perfect matches...</p>
          </div>
        ) : matches.length > 0 ? (
          <table className="recent-table matches-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>S.No.</th>
                <th style={{ width: '120px' }}>Match ID</th>
                <th style={{ width: '25%' }}>Profile 1 (Male)</th>
                <th style={{ width: '25%' }}>Profile 2 (Female)</th>
                <th style={{ width: '90px' }}>Match %</th>
                <th style={{ width: '110px' }}>Match Date</th>
                <th style={{ width: '100px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((match, index) => {
                const statusStyle = getStatusStyle(match.status);
                return (
                  <tr key={match.id || index}>
                    <td>{index + 1}</td>
                    <td className="ref-id">{match.match_id}</td>
                    
                    {/* Profile 1 (Male) */}
                    <td className="profile-cell">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img 
                          src={match.profile1_image} 
                          alt={match.profile1_name}
                          style={{ 
                            width: 40, height: 40, borderRadius: '50%', 
                            objectFit: 'cover', border: '2px solid #bfdbfe',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${match.profile1_name}&background=3b82f6&color=fff`;
                          }}
                        />
                        <div className="profile-info" style={{ minWidth: 0, flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}>
                            <span className="profile-name" style={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 700
                            }}>
                              {match.profile1_name}
                            </span>
                            <span style={{ 
                              fontSize: 10, color: '#3b82f6', 
                              fontWeight: 600, flexShrink: 0
                            }}>
                              ♂ {match.profile1_age}y
                            </span>
                          </div>
                          <span className="profile-ref" style={{ 
                            display: 'block', fontSize: 11, 
                            color: '#666', marginTop: 2 
                          }}>
                            {match.profile1_profile_id}
                          </span>
                          {match.profile1_city && (
                            <span style={{ 
                              display: 'block', fontSize: 10, 
                              color: '#888', marginTop: 2 
                            }}>
                              📍 {match.profile1_city}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    {/* Profile 2 (Female) */}
                    <td className="profile-cell">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img 
                          src={match.profile2_image} 
                          alt={match.profile2_name}
                          style={{ 
                            width: 40, height: 40, borderRadius: '50%', 
                            objectFit: 'cover', border: '2px solid #fce7f3',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${match.profile2_name}&background=ec4899&color=fff`;
                          }}
                        />
                        <div className="profile-info" style={{ minWidth: 0, flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}>
                            <span className="profile-name" style={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 700
                            }}>
                              {match.profile2_name}
                            </span>
                            <span style={{ 
                              fontSize: 10, color: '#ec4899', 
                              fontWeight: 600, flexShrink: 0
                            }}>
                              ♀ {match.profile2_age}y
                            </span>
                          </div>
                          <span className="profile-ref" style={{ 
                            display: 'block', fontSize: 11, 
                            color: '#666', marginTop: 2 
                          }}>
                            {match.profile2_profile_id}
                          </span>
                          {match.profile2_city && (
                            <span style={{ 
                              display: 'block', fontSize: 10, 
                              color: '#888', marginTop: 2 
                            }}>
                              📍 {match.profile2_city}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    {/* Match Percentage */}
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '5px 12px',
                        background: match.match_percentage >= 80 
                          ? 'linear-gradient(135deg, #4caf50, #66bb6a)'
                          : match.match_percentage >= 60
                          ? 'linear-gradient(135deg, #ff9800, #ffb74d)'
                          : 'linear-gradient(135deg, #9e9e9e, #bdbdbd)',
                        color: '#fff',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 800,
                      }}>
                        💖 {match.match_percentage}%
                      </span>
                    </td>
                    
                    <td className="date-cell">
                      {formatDate(match.matchDate)}
                    </td>
                    
                    <td>
                      <span 
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          border: `1px solid ${statusStyle.border}`,
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: 'capitalize',
                        }}
                      >
                        {match.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="matches-empty">
            <Empty 
              description={
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#666', marginBottom: 8, fontWeight: 600 }}>
                    No matches found
                  </p>
                  <p style={{ color: '#999', fontSize: 12 }}>
                    Users need same religion + caste to match
                  </p>
                </div>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default RecentMatches;